import React, { useMemo } from 'react';
import { Trophy, Video, Instagram } from 'lucide-react';
import _ from 'lodash';
import { CLEAN_BOULDER_ASCENTS, CLEAN_SPORT_ASCENTS, CLEAN_TRAD_ASCENTS } from '@utils/ascentTypes.js';

// Extract video URL from comment (YouTube or Vimeo)
const extractVideoUrl = (comment) => {
  if (!comment) return null;
  // Match YouTube URLs (youtube.com/watch?v= or youtu.be/)
  const youtubeMatch = comment.match(/https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)[^\s"]+/);
  if (youtubeMatch) return youtubeMatch[0];
  // Match Vimeo URLs
  const vimeoMatch = comment.match(/https?:\/\/(?:www\.)?vimeo\.com\/[^\s"]+/);
  if (vimeoMatch) return vimeoMatch[0];
  return null;
};

// Extract Instagram URL from comment (posts or reels)
const extractInstagramUrl = (comment) => {
  if (!comment) return null;
  const instagramMatch = comment.match(/https?:\/\/(?:www\.)?instagram\.com\/(?:p|reel)\/[^\s"]+/);
  if (instagramMatch) return instagramMatch[0];
  return null;
};

// Add 8a.nu scoring function
const calculate8aScore = (grade, ascentType, climbingStyle, is2ndGo) => {
  let score = 0;

  if (climbingStyle === 'boulder') {
    // Convert V-grade to approximate ewbank for scoring
    // V0 ≈ 21/22 Ewbank, each V-grade is roughly 1 Ewbank grade
    const approximateEwbank = grade <= 0 ? 21 : 21 + grade;
    score = (approximateEwbank - 29) * 50 + 1000;
    if (approximateEwbank < 22) score = score - 50;
  } else {
    // Sport climbing
    score = (Math.round(grade) - 29) * 50 + 1000;
    if (grade < 22) score = score - 50;
  }

  // Add bonuses for flash/onsight/2nd go
  if (ascentType === "Onsight") {
    score = score + 150 - 3;
  } else if (ascentType === "Flash") {
    score = score + 53;
  } else if (is2ndGo) {
    // 2nd go redpoint bonus
    score = score + 2;
  }

  return score;
};

const BestClimbsChart = ({ 
  data, 
  climbingStyle, 
  getGradeLabel,
  gradeSystem
}) => {
  const bestClimbs = useMemo(() => {
    if (!data?.length) return [];

    // Group ascents by Route ID
    const groupedAscents = _.groupBy(data, 'Route ID');
    
    // Process each route to find first clean ascent and count attempts
    const processedAscents = Object.entries(groupedAscents).map(([routeId, attempts]) => {
      // Sort attempts by date
      const sortedAttempts = _.sortBy(attempts, 'Ascent Date');
      
      // Find first clean ascent
      const firstCleanAscent = sortedAttempts.find(attempt => {
        const cleanAscentTypes = climbingStyle === 'boulder' ? 
          CLEAN_BOULDER_ASCENTS : 
          [...CLEAN_SPORT_ASCENTS, ...CLEAN_TRAD_ASCENTS];
        return cleanAscentTypes.includes(attempt["Ascent Type"]);
      });

      if (!firstCleanAscent) return null;

      const firstCleanDate = new Date(firstCleanAscent["Ascent Date"]);
      
      const grade = climbingStyle === 'boulder' ? 
        firstCleanAscent.vgrade : 
        firstCleanAscent.ewbanks;
      
      if (grade === null || isNaN(grade)) return null;

      // Calculate 8a.nu score (include 2nd go bonus)
      // is2ndGo is pre-computed during export (from attemptNumber === 2 OR #2ndGo in comment)
      const comment = firstCleanAscent["Ascent Comment"] || '';
      const is2ndGo = firstCleanAscent.is2ndGo ||
        firstCleanAscent.attemptNumber === 2 ||
        comment.includes('#2ndGo');
      const score = calculate8aScore(grade, firstCleanAscent["Ascent Type"], climbingStyle, is2ndGo);

      // Extract video and Instagram URLs from comment
      const videoUrl = extractVideoUrl(comment);
      const instagramUrl = extractInstagramUrl(comment);

      return {
        name: firstCleanAscent["Route Name"],
        crag: firstCleanAscent["Crag Name"],
        grade: firstCleanAscent["Route Grade"],
        originalGrade: grade,
        date: firstCleanDate,
        type: firstCleanAscent["Ascent Type"],
        score,
        is2ndGo,
        videoUrl,
        instagramUrl,
        attempts: 1 // Since 8a.nu only counts the successful ascent
      };
    }).filter(ascent => ascent !== null);

    // Sort by 8a.nu score, with media availability as tiebreaker
    return processedAscents
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        // Prefer climbs with video/instagram when scores are equal
        const aHasMedia = a.videoUrl || a.instagramUrl;
        const bHasMedia = b.videoUrl || b.instagramUrl;
        if (aHasMedia && !bHasMedia) return -1;
        if (bHasMedia && !aHasMedia) return 1;
        return 0;
      })
      .slice(0, 10);
  }, [data, climbingStyle]);

  const getMedalColor = (index) => {
    switch (index) {
      case 0: return 'text-yellow-400';
      case 1: return 'text-gray-400';
      case 2: return 'text-amber-600';
      default: return 'text-gray-300';
    }
  };

  return (
    <div className="h-full overflow-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-white shadow-sm">
          <tr className="bg-gray-50">
            <th className="px-3 py-2 text-left">Rank</th>
            <th className="px-3 py-2 text-left">Route</th>
            <th className="px-3 py-2 text-left">Grade</th>
            <th className="px-3 py-2 text-left">Type</th>
            <th className="px-3 py-2 text-left">Score</th>
            <th className="px-3 py-2 text-left">Date</th>
          </tr>
        </thead>
        <tbody>
          {bestClimbs.map((climb, index) => (
            <tr 
              key={`${climb.name}-${climb.date}`}
              className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
            >
              <td className="px-3 py-1.5">
                <div className="flex items-center gap-2">
                  <Trophy className={`w-4 h-4 ${getMedalColor(index)}`} />
                  {index + 1}
                </div>
              </td>
              <td className="px-3 py-1.5">
                <div>
                  <div className="font-medium flex items-center gap-1">
                    {climb.name}
                    {climb.videoUrl && (
                      <a
                        href={climb.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-red-500 hover:text-red-700"
                        title="Watch video"
                      >
                        <Video className="w-4 h-4" />
                      </a>
                    )}
                    {climb.instagramUrl && (
                      <a
                        href={climb.instagramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-pink-500 hover:text-pink-700"
                        title="View on Instagram"
                      >
                        <Instagram className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">{climb.crag}</div>
                </div>
              </td>
              <td className="px-3 py-1.5">{climb.grade}</td>
              <td className="px-3 py-1.5">
                {climb.type}
                {climb.is2ndGo && <span className="text-gray-500 ml-1">(2nd Go)</span>}
              </td>
              <td className="px-3 py-1.5">{Math.round(climb.score)}</td>
              <td className="px-3 py-1.5">
                {climb.date.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </td>
            </tr>
          ))}
        </tbody>
        {bestClimbs.length > 0 && (
          <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_rgba(0,0,0,0.1)]">
            <tr className="bg-gray-100 font-semibold">
              <td className="px-3 py-2" colSpan={4}>Total</td>
              <td className="px-3 py-2">
                {Math.round(bestClimbs.reduce((sum, c) => sum + c.score, 0))}
              </td>
              <td className="px-3 py-2"></td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

export default BestClimbsChart;